import React from 'react';
import {
  FaVideo, FaCalendarAlt, FaClock, FaCheck, FaGraduationCap
} from 'react-icons/fa';

const SEED_IDS = ['webinar_001','webinar_002','webinar_003','webinar_004','webinar_005','webinar_006'];

export function WebinarsTab({ webinars, onRegister, onUnregister }) {
  const realWebinars = webinars.filter(w => w.status === 'upcoming' && !SEED_IDS.includes(w.webinar_id));
  const sampleWebinars = webinars.filter(w => SEED_IDS.includes(w.webinar_id));
  const pastWebinars = webinars.filter(w => w.status === 'completed' && !SEED_IDS.includes(w.webinar_id));

  return (
    <div data-testid="webinars-tab">
      <h2 className="text-2xl font-bold text-gray-800 mb-2">Upcoming Webinars</h2>
      <p className="text-gray-600 mb-6">Join live sessions with industry experts</p>

      <div className="grid md:grid-cols-2 gap-6 mb-8">
        {realWebinars.map((webinar) => (
          <WebinarCard
            key={webinar.webinar_id}
            webinar={webinar}
            onRegister={onRegister}
            onUnregister={onUnregister}
          />
        ))}
      </div>

      {realWebinars.length === 0 && (
        <div className="text-center py-12 bg-white rounded-xl mb-8">
          <FaVideo className="text-gray-300 text-5xl mx-auto mb-4" />
          <p className="text-gray-600">Webinars are in the planning stages</p>
          <p className="text-sm text-gray-500 mt-2">Stay tuned as our advisors prepare upcoming sessions for Legacy Forge families</p>
        </div>
      )}

      {sampleWebinars.length > 0 && (
        <div className="mt-8 mb-8">
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-6">
            <div className="flex items-center gap-2 mb-1">
              <FaGraduationCap className="text-slate-500" />
              <h3 className="text-lg font-bold text-slate-700">Webinar Topics Our Advisors May Consider Offering</h3>
            </div>
            <p className="text-slate-500 text-sm mb-5">
              These are examples of the types of sessions our professional advisors can host for Legacy Forge families.
              Interested in a topic? Let your advisor know.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              {sampleWebinars.map((webinar) => (
                <div
                  key={webinar.webinar_id}
                  className="bg-white rounded-lg border border-slate-200 overflow-hidden"
                  data-testid={`sample-webinar-${webinar.webinar_id}`}
                >
                  <div className="bg-gradient-to-r from-slate-400 to-slate-500 text-white px-4 py-3">
                    <span className="text-xs font-medium bg-white/20 px-2 py-0.5 rounded">{webinar.category}</span>
                    <h4 className="font-bold mt-1">{webinar.title}</h4>
                  </div>
                  <div className="p-4">
                    <p className="text-gray-600 text-sm mb-3">{webinar.description}</p>
                    <div className="flex items-center text-gray-500 text-sm">
                      <FaClock className="mr-1 text-gray-400" />
                      <span>{webinar.duration_minutes} min session</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {pastWebinars.length > 0 && (
        <>
          <h3 className="text-xl font-bold text-gray-800 mb-4 mt-8">Past Recordings</h3>
          <div className="grid md:grid-cols-2 gap-4">
            {pastWebinars.map((webinar) => (
              <div key={webinar.webinar_id} className="bg-white rounded-lg p-4 shadow-sm flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-gray-800">{webinar.title}</h4>
                  <p className="text-sm text-gray-500">{webinar.presenter}</p>
                </div>
                {webinar.recording_url && (
                  <button className="text-blue-600 hover:text-blue-700 font-medium">
                    Watch Recording
                  </button>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function WebinarCard({ webinar, onRegister, onUnregister }) {
  const spotsLeft = webinar.max_attendees ? webinar.max_attendees - webinar.registered_count : null;
  const isFull = spotsLeft !== null && spotsLeft <= 0;

  return (
    <div
      className="bg-white rounded-xl shadow-md overflow-hidden"
      data-testid={`webinar-card-${webinar.webinar_id}`}
    >
      <div className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white p-4">
        <span className="text-xs font-medium bg-white/20 px-2 py-1 rounded">{webinar.category}</span>
        <h3 className="text-lg font-bold mt-2">{webinar.title}</h3>
      </div>

      <div className="p-5">
        <p className="text-gray-600 text-sm mb-4">{webinar.description}</p>

        <div className="flex items-center text-gray-700 mb-2">
          <FaGraduationCap className="mr-2 text-gray-400" />
          <span className="font-medium">{webinar.presenter}</span>
          {webinar.presenter_title && (
            <span className="text-sm text-gray-500 ml-2">• {webinar.presenter_title}</span>
          )}
        </div>

        <div className="flex items-center text-gray-600 text-sm mb-2">
          <FaCalendarAlt className="mr-2 text-gray-400" />
          <span>{new Date(webinar.date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</span>
        </div>

        <div className="flex items-center text-gray-600 text-sm mb-4">
          <FaClock className="mr-2 text-gray-400" />
          <span>{webinar.time} • {webinar.duration_minutes} minutes</span>
        </div>

        <div className="flex items-center justify-between pt-4 border-t">
          <div className="text-sm">
            <span className="font-medium text-gray-800">{webinar.registered_count}</span>
            <span className="text-gray-500"> registered</span>
            {spotsLeft !== null && spotsLeft <= 20 && spotsLeft > 0 && (
              <span className="text-amber-600 ml-2">• {spotsLeft} spots left</span>
            )}
            {isFull && <span className="text-red-500 ml-2">• Full</span>}
          </div>

          {webinar.is_registered ? (
            <button
              onClick={() => onUnregister(webinar.webinar_id)}
              className="flex items-center space-x-2 px-4 py-2 bg-green-50 text-green-700 rounded-lg hover:bg-green-100"
              data-testid={`unregister-btn-${webinar.webinar_id}`}
            >
              <FaCheck />
              <span>Registered</span>
            </button>
          ) : (
            <button
              onClick={() => onRegister(webinar.webinar_id)}
              disabled={isFull}
              className={`px-4 py-2 rounded-lg font-medium ${
                isFull
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-indigo-600 text-white hover:bg-indigo-700'
              }`}
              data-testid={`register-btn-${webinar.webinar_id}`}
            >
              {isFull ? 'Full' : 'Register Free'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
